import React, { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { useApp } from "@/state/AppContext";

/** A standing "you are offline" strip.
 *
 * The service worker serves the cached app shell for any navigation that fails, which is the
 * right behaviour for a client-routed app — the URL is preserved and the router still renders
 * the page you asked for. What was missing is any hint about WHY that page then has no data:
 * `/api` is deliberately excluded from the cache (stale orders and stale money are worse than
 * none), so every request behind the shell fails and each screen reports its own error as though
 * the server were at fault.
 *
 * One honest line at the top costs nothing and explains all of them at once.
 */
export function OfflineBanner() {
  const { t } = useApp();
  // Read once at mount rather than defaulting to "online": the app can be opened cold from the
  // home screen with no connection, and in that case there is no `offline` event to wait for.
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 bg-muted px-4 py-2 text-center text-sm text-muted-foreground"
      data-testid="offline-banner"
    >
      <WifiOff size={14} aria-hidden="true" />
      {t("common.offline")}
    </div>
  );
}
